import { format, subDays, differenceInMinutes } from 'date-fns';
import api from '@/api/schedule';
import apiAvail from '@/api/calendar';
import { postProcessEvents } from '@/utils/calendar';

const actions = {
  FETCH_CLASSES: async ({ commit, dispatch, state, rootState }) => {
    const { currentPage, pageSize, ordering, filters, dateRange } = state;
    const offset = currentPage * pageSize;
    const params = {
      limit: pageSize,
      offset,
      ordering,
      ...(dateRange ? { ...dateRange } : {}),
      ...filters,
      only_my_classes: true,
    };

    dispatch('wait/start', 'schedule.fetching', { root: true });

    try {
      const { count, results: classes } = await api.fetchClasses(params);

      if (classes.length > 0) {
        const timeZoneEntry = classes.find(entry => entry.timezone);
        const timeZone = timeZoneEntry ? timeZoneEntry.timezone : null;
        const route = rootState.route.name;

        if (timeZone) {
          commit('timezones/SET_ROUTE_TIMEZONE', { route, timeZone }, { root: true });
        }
      }

      commit('SET_CLASSES', classes);
      commit('SET_TOTAL_COUNT', count);
      commit('SET_REFRESH_DATE', Date.now());
    } finally {
      dispatch('wait/end', 'schedule.fetching', { root: true });
    }
  },

  DOWNLOAD_CLASSES: async ({ getters, state }, fileFormat) => {
    const { tutorId } = getters;

    if (!tutorId) {
      return;
    }

    const fields = state.columns.filter(column => column.showColumn).map(column => column.source_field);
    const { currentPage, pageSize, ordering, filters, dateRange } = state;
    const offset = currentPage * pageSize;

    const params = {
      limit: pageSize,
      offset,
      ordering,
      ...(dateRange ? { ...dateRange } : {}),
      ...filters,
      format: fileFormat,
      include: fields,
    };

    await api.downloadClasses(tutorId, params);
  },

  FETCH_CLASSES_MISSING_FEEDBACK_COUNT: async ({ commit, dispatch }) => {
    const endDate = format(new Date(), 'yyyy-MM-dd');
    const startDate = format(subDays(new Date(), 90), 'yyyy-MM-dd');

    const params = {
      start_date: startDate,
      end_date: endDate,
    };

    dispatch('wait/start', 'schedule.fetching-missing-feedback-count', { root: true });

    try {
      const { count: missingFeedbackClassesCount } = await api.fetchPendingFeedbacks(params);
      commit('SET_MISSING_FEEDBACK_COUNT', missingFeedbackClassesCount);
    } finally {
      dispatch('wait/end', 'schedule.fetching-missing-feedback-count', { root: true });
    }
  },

  FETCH_MISSING_FEEDBACK_CLASSES: async ({ commit, dispatch }) => {
    const endDate = format(new Date(), 'yyyy-MM-dd');
    const startDate = format(subDays(new Date(), 90), 'yyyy-MM-dd');
    const dateRange = { start: startDate, end: endDate };

    commit('SET_ORDERING', 'scheduled_at');
    commit('SET_DATE_RANGE', dateRange);
    commit('SET_CURRENT_PAGE', 0);

    const newFilters = [
      { key: 'webackend_class_type', value: '1_1' },
      { key: 'is_feedback_submitted', value: false },
    ];

    newFilters.forEach(filter => {
      commit('SET_FILTER', filter);
    });

    dispatch('FETCH_CLASSES');
  },

  FETCH_NEAREST_CLASSES: async ({ commit, dispatch }) => {
    const params = {
      limit: 5,
      offset: 0,
      order_by: 'scheduled_at',
      only_my_classes: true,
    };

    dispatch('wait/start', 'schedule.fetching-nearest', { root: true });

    try {
      const { results: nearestClasses } = await api.fetchClasses(params);
      commit('SET_NEAREST_CLASSES', nearestClasses);
    } finally {
      dispatch('wait/end', 'schedule.fetching-nearest', { root: true });
    }
  },

  FETCH_AVAILABLE_SLOTS: async ({ getters }, { start, end, teacherHashEmail }) => {
    const { tutorEmail } = getters;
    const useEmail = teacherHashEmail || tutorEmail;

    const data = await apiAvail.fetchSlots(start, end, useEmail);
    const parsedData = postProcessEvents(data);
    return parsedData;
  },

  ADD_AVAILABLE_SLOTS: async ({ getters, rootGetters }, { start, end, teacherHashEmail, orgId, isAdmin }) => {
    const { tutorEmail } = getters;
    const { userProfile } = rootGetters;
    const duration = differenceInMinutes(end, start);
    const useEmail = teacherHashEmail || tutorEmail;
    const useOrgId = orgId || userProfile.organization.id;

    await apiAvail.addSlots(start, duration, userProfile.timezone, useEmail, useOrgId, isAdmin);
  },

  DELETE_AVAILABLE_SLOTS: async ({ getters }, { event, teacherHashEmail }) => {
    const { tutorEmail } = getters;
    const { start, end } = event;
    const duration = differenceInMinutes(end, start);
    const useEmail = teacherHashEmail || tutorEmail;

    await apiAvail.deleteSlots(start, duration, useEmail);
  },

  REPLICATE_WEEKS: async ({ getters, rootGetters }, { weeks, start, teacherHashEmail, orgId, isAdmin }) => {
    const { tutorEmail } = getters;
    const { userProfile } = rootGetters;
    const useEmail = teacherHashEmail || tutorEmail;
    const useOrgId = orgId || userProfile.organization.id;

    await apiAvail.replicateWeeks(weeks, start, userProfile.timezone, useEmail, useOrgId, isAdmin);
  },

  APPLY_FILTER: ({ commit, dispatch }, filter) => {
    commit('SET_FILTER', filter);
    dispatch('FETCH_CLASSES');
  },

  RESET_FILTERS: ({ commit, dispatch }) => {
    commit('RESET_FILTERS');
    dispatch('FETCH_CLASSES');
  },

  CHANGE_ORDERING: ({ commit, dispatch, state }, { ordering }) => {
    if (ordering === state.ordering) {
      return;
    }

    commit('SET_ORDERING', ordering);
    commit('SET_CURRENT_PAGE', 0);
    dispatch('FETCH_CLASSES');
  },

  CHANGE_PAGE_SIZE: ({ commit, dispatch }, { pageSize }) => {
    commit('SET_PAGE_SIZE', pageSize);
    commit('SET_CURRENT_PAGE', 0);
    dispatch('FETCH_CLASSES');
  },

  CHANGE_CURRENT_PAGE: ({ commit, dispatch }, { currentPage }) => {
    commit('SET_CURRENT_PAGE', currentPage);
    dispatch('FETCH_CLASSES');
  },

  CHANGE_DATE_RANGE: ({ commit, dispatch }, dateRange) => {
    commit('SET_DATE_RANGE', dateRange);
    commit('SET_CURRENT_PAGE', 0);
    dispatch('FETCH_CLASSES');
  },
};

export default actions;
